import React, { type ReactNode, useRef, useEffect, useState } from 'react';

import { TooltipProvider } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { ConversationProvider } from './ConversationContext';
import { ConversationMessages } from './ConversationMessages';
import { ConversationInput } from './ConversationInput';
import { ConversationSuggestions } from './ConversationSuggestions';
import { ConversationActions } from './ConversationActions';
import { ConversationStatus } from './ConversationStatus';
import type {
  Message,
  ConversationCallbacks,
  Suggestion,
  ConversationAction,
  Attachment,
  Collection,
  ContextPill,
} from './types';
import { Logo } from '../logo';

interface ConversationProps {
  messages: Message[];
  callbacks: ConversationCallbacks;
  children?: ReactNode;
  // Props for simple (non-compound) mode
  suggestions?: Suggestion[];
  actions?: ConversationAction[];
  placeholder?: string;
  attachments?: Attachment[];
  collections?: Collection[];
  contextPills?: ContextPill[];
  showBranding?: boolean;
  className?: string;
}

const ConversationRoot = ({
  messages,
  callbacks,
  children,
  suggestions = [],
  actions = [],
  placeholder,
  attachments,
  collections,
  contextPills,
  showBranding = false,
  className,
}: ConversationProps) => {
  const isCompound = !!children;
  const bottomRef = useRef<HTMLDivElement>(null);
  const [bottomHeight, setBottomHeight] = useState(0);
  const [hasContentBehind, setContentBehind] = useState(false);

  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBottomHeight(entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <TooltipProvider>
      <ConversationProvider messages={messages} callbacks={callbacks}>
        <div className={cn('flex h-full flex-col pb-4', className)}>
          {isCompound ? (
            children
          ) : (
            <>
              <div className="relative min-h-0 flex-1 grid grid-rows-[1fr] grid-cols-[1fr]">
                <div className="row-start-1 col-start-1 min-h-0">
                  <ConversationMessages bottomPadding={bottomHeight} onContentBehind={setContentBehind} />
                </div>
                <div ref={bottomRef} className="row-start-1 col-start-1 self-end z-10 pointer-events-none px-4">
                  <div className="pointer-events-auto">
                    {suggestions.length > 0 && <ConversationSuggestions items={suggestions} />}
                    {actions.length > 0 && (
                      <ConversationActions
                        actions={actions}
                        onActionClick={(action) => callbacks.onActionClick?.(action, '')}
                      />
                    )}
                    <ConversationInput
                      placeholder={placeholder}
                      attachments={attachments}
                      collections={collections}
                      contextPills={contextPills}
                      isElevated={hasContentBehind}
                    />
                  </div>
                </div>
              </div>
              {showBranding && (
                <div className="relative z-10 pt-2">
                  <Branding />
                </div>
              )}
            </>
          )}
        </div>
      </ConversationProvider>
    </TooltipProvider>
  );
};

const Branding = () => (
  <div className="flex items-center justify-center gap-1 text-[0.625rem] text-subtle">
    <span>Powered by</span>
    <Logo variant="full" color="outline" className="h-2.5 w-auto" />
  </div>
);

export const Conversation = Object.assign(ConversationRoot, {
  Messages: ConversationMessages,
  Input: ConversationInput,
  Suggestions: ConversationSuggestions,
  Actions: ConversationActions,
  Loading: ConversationStatus,
});
